import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Navigate, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

function DeleteConfirmModal({ company, onClose, onConfirm }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/api/companies/${company.id}/`);
      onConfirm();
    } catch {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-sm mx-4 animate-fade-in border border-border">
        <div className="p-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
            <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-lg font-semibold mb-2">{t("company.deleteTitle")}</h2>
          <p className="text-sm text-muted-foreground mb-6">
            {t("common.confirmRemove")} <strong>{company.name}</strong>?
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              loading={loading}
            >
              {t("common.remove")}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Companies() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingCompany, setDeletingCompany] = useState(null);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await api.get("/api/companies/");
      setCompanies(res.data);
    } catch {
      // handle error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_admin) fetchCompanies();
  }, [fetchCompanies, user]);

  if (!user?.is_admin) {
    return <Navigate to="/liveness/admin/client-apps" replace />;
  }

  const handleDeleted = () => {
    setDeletingCompany(null);
    fetchCompanies();
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("nav.companies")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("company.listSubtitle")}
          </p>
        </div>
        <Button onClick={() => navigate("/liveness/admin/companies/new")}>
          <Plus className="h-4 w-4 mr-2" />
          {t("company.new")}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {t("company.empty")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("common.name")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("common.language")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("common.country")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("company.createdAt")}
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => (
                    <tr
                      key={company.id}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium">{company.name}</td>
                      <td className="px-4 py-3 text-muted-foreground uppercase">
                        {company.language}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground uppercase">
                        {company.country || "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(company.created).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() =>
                              navigate(`/liveness/admin/companies/${company.id}/edit`)
                            }
                            className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            title={t("common.edit")}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeletingCompany(company)}
                            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-muted-foreground hover:text-red-600"
                            title={t("common.remove")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {deletingCompany && (
        <DeleteConfirmModal
          company={deletingCompany}
          onClose={() => setDeletingCompany(null)}
          onConfirm={handleDeleted}
        />
      )}
    </div>
  );
}
